import React, { useEffect, useState } from 'react';
import { Box, Text, VStack, Link, Icon, HStack, useColorModeValue } from '@chakra-ui/react';
import { LuList } from 'react-icons/lu';
import { extrairSumario } from './documentUtils';

/**
 * Sumário do documento montado a partir dos headings.
 * Marca a seção visível durante a rolagem e some quando há menos de 2 títulos.
 */
const WikiTOC = ({ content, containerRef, colorScheme = 'orange' }) => {
  const [ativo, setAtivo] = useState(null);
  const secoes = extrairSumario(content);

  const corTexto = useColorModeValue('gray.600', 'gray.400');
  const corAtiva = useColorModeValue(`${colorScheme}.600`, `${colorScheme}.200`);
  const bgAtiva = useColorModeValue(`${colorScheme}.50`, 'whiteAlpha.100');
  const borda = useColorModeValue('gray.200', 'gray.700');

  // Marca os headings renderizados com os mesmos ids do sumário
  useEffect(() => {
    const raiz = containerRef?.current;
    if (!raiz || secoes.length === 0) return;

    const titulos = raiz.querySelectorAll('h1, h2, h3');
    titulos.forEach((el, i) => {
      if (secoes[i]) el.id = secoes[i].id;
    });

    const observer = new IntersectionObserver(
      (entradas) => {
        const visivel = entradas.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visivel?.target?.id) setAtivo(visivel.target.id);
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 }
    );

    titulos.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [content, containerRef, secoes.length]); // eslint-disable-line react-hooks/exhaustive-deps

  if (secoes.length < 2) return null;

  const irPara = (e, id) => {
    e.preventDefault();
    const alvo = document.getElementById(id);
    if (alvo) {
      alvo.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setAtivo(id);
    }
  };

  return (
    <Box
      className="wiki-toc"
      // telas menores: cartão acima do texto | xl: coluna lateral fixa
      border={{ base: '1px solid', xl: 'none' }}
      borderLeft={{ base: '1px solid', xl: '1px solid' }}
      borderColor={borda}
      borderRadius={{ base: 'md', xl: 'none' }}
      p={{ base: 3, xl: 0 }}
      pl={{ base: 3, xl: 4 }}
    >
      <HStack spacing={2} mb={2}>
        <Icon as={LuList} boxSize={3.5} color={corTexto} />
        <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color={corTexto} letterSpacing="wide">
          Nesta página
        </Text>
      </HStack>

      <VStack align="stretch" spacing={0} maxH={{ base: '40vh', xl: 'calc(100vh - 220px)' }} overflowY="auto">
        {secoes.map((sec) => (
          <Link
            key={sec.id}
            href={`#${sec.id}`}
            onClick={(e) => irPara(e, sec.id)}
            fontSize="xs"
            lineHeight="1.4"
            py={1.5}
            px={2}
            pl={`${(sec.nivel - 1) * 12 + 8}px`}
            borderRadius="md"
            color={ativo === sec.id ? corAtiva : corTexto}
            bg={ativo === sec.id ? bgAtiva : 'transparent'}
            fontWeight={ativo === sec.id ? 'bold' : 'normal'}
            _hover={{ color: corAtiva, textDecoration: 'none', bg: bgAtiva }}
            transition="all 0.15s"
            noOfLines={2}
          >
            {sec.texto}
          </Link>
        ))}
      </VStack>
    </Box>
  );
};

export default WikiTOC;
